import React, { useEffect, useMemo, memo, useState, useCallback } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import {
  createSession,
  getAppealId,
  getAppealsFormError,
  getAppealsFormData,
  getVisibleFormData,
} from './selectors'
import { DefaultJsonFormContextProps } from '../../../components/v2/default-json-form/context/default-json-form-context-props'
import { getAppealsEnums, getAppealsForm } from '../../../store/appeal/action-creators'
import DefaultJsonFormContext from '../../../components/v2/default-json-form/context/default-json-form-context'
import { AppealsTabs } from './ui/tabs/appeals-tabs'
import { useAppDispatch } from '../../../hooks/useAppDispatch/useAppDispatch'
import AppealsFormsHeader from './ui/appeals-forms/appeals-form-header/appeals-form-header'
import { useSaveResult } from './hooks/useSaveResult'
import { Modal } from 'antd'
import { clearAppealsFormFailed } from '../../../store/appeal/actions'
import { AppealCreateOnboardingTour } from '../../../utilits/onboarding/tours/appeal-create-onboarding-tour'
import { AppealCreateSuccessToast } from '../../../utilits/onboarding/tours/appeal-create-success-toast'
import { AppealCardOnboardingTour } from '../../../utilits/onboarding/tours/appeal-card-onboarding-tour'
import { updateTourProgress } from '../../../store/session/action-creators'

const CREATE_TOUR_ID = 'tour.appeals.create.v1'

const AppealCardPage = memo(() => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const appealId = useSelector(getAppealId)
  const entityType = 'appeal'
  const [modal, contextHolder] = Modal.useModal()
  const appealsFormError = useSelector(getAppealsFormError)
  const { sessionData } = useSelector(createSession)
  const appealsFormData = useSelector(getAppealsFormData)
  const visibleFormData = useSelector(getVisibleFormData)
  const { saveResult } = useSaveResult()
  const [isFormProcessing, setIsFormProcessing] = useState(false)
  const tours = (sessionData as any)?.jsonSettings?.tours || {}
  const createTourProgress = tours?.[CREATE_TOUR_ID] || {}

  const pageTitle = useMemo(() => (id === 'new' ? 'Новое обращение' : `Обращение №${id}`), [id])

  const breadcrumbs = useMemo(
    () => [
      {
        href: '/appeals',
        title: 'Обращения',
      },
      {
        title: pageTitle,
      },
    ],
    [pageTitle],
  )

  const defaultProps = useMemo(
    (): DefaultJsonFormContextProps => ({
      entityId: id,
      entityType: entityType,
    }),
    [id],
  )

  useEffect(() => {
    let mounted = true
    if (mounted) {
      const getInitPage = async id => {
        await dispatch(getAppealsEnums(id))
        await dispatch(getAppealsForm(id))
      }
      getInitPage(id).catch(console.error)
    }
    return () => {
      mounted = false
    }
  }, [id, dispatch])

  useEffect(() => {
    if (id === 'new' && appealId) {
      // Автозавершение тура создания по факту сохранения (редирект на /appeal/:id),
      // чтобы пользователь не зависел от клика "Завершить".
      if (!createTourProgress?.completed && createTourProgress?.stage !== 'waiting_save') {
        dispatch(
          updateTourProgress(CREATE_TOUR_ID, {
            stage: 'waiting_save',
            completed: false,
            createdAppealId: appealId,
          }) as any,
        )
      }
      navigate({
        pathname: `/appeal/${appealId}`,
      })
    }
  }, [id, appealId, dispatch, navigate, createTourProgress?.completed, createTourProgress?.stage])

  const handleToOrder = useCallback(async () => {
    setIsFormProcessing(true)
    try {
      await saveResult(id, visibleFormData, appealsFormData?.stages?.current, 'order')
    } finally {
      setIsFormProcessing(false)
    }
  }, [id, visibleFormData, appealsFormData, saveResult])

  useEffect(() => {
    if (appealsFormError) {
      modal.error({
        title: 'Ошибка',
        content: appealsFormError,
        onOk: () => dispatch(clearAppealsFormFailed()),
      })
    }
  }, [appealsFormError])

  if (!id) return null
  return (
    <B2BLayout breadcrumbs={breadcrumbs} pageTitle={pageTitle}>
      <AppealCreateOnboardingTour isNew={id === 'new'} />
      <AppealCreateSuccessToast />
      {id !== 'new' && <AppealCardOnboardingTour />}
      <DefaultJsonFormContext.Provider value={defaultProps}>
        <AppealsFormsHeader
          id={id}
          isFinished={
            appealsFormData?.stages &&
            appealsFormData?.stages?.current ===
              Object.keys(appealsFormData?.stages?.items).length - 1
          }
          stage={appealsFormData?.stages?.current}
          toOrder={handleToOrder}
          isFormProcessing={isFormProcessing}
        />
        <AppealsTabs id={id} isFormProcessing={isFormProcessing} />
      </DefaultJsonFormContext.Provider>
      {contextHolder}
    </B2BLayout>
  )
})

AppealCardPage.displayName = 'AppealCardPage'
export default memo(AppealCardPage)
